import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Send,
  Download,
  Eye,
  Plus,
  Trash2,
  Calculator,
  DollarSign,
  Percent,
  Calendar,
  FileText,
  Check,
  X,
  MessageCircle
} from 'lucide-react'
import { useApp } from '../context/AppContext'

export default function InvoiceEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    clients,
    settings,
    addInvoice,
    updateInvoice,
    getInvoiceById,
    getClientById,
    addNotification
  } = useApp()

  const isEditing = Boolean(id)
  const existingInvoice = isEditing ? getInvoiceById(id) : null

  // Form state
  const [formData, setFormData] = useState({
    clientId: '',
    subject: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + (parseInt(settings?.paymentTerms) || 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [
      { id: 'item_1', description: '', quantity: 1, rate: 0, tax: 0 }
    ],
    taxRate: settings?.taxRate || 0,
    taxLabel: settings?.taxLabel || 'Tax',
    discount: 0,
    discountType: 'fixed',
    notes: 'Thank you for your business!',
    terms: settings?.paymentTerms === '0'
      ? 'Payment is due on receipt.'
      : `Payment is due within ${settings?.paymentTerms || 30} days of invoice date.`
  })

  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  // Load existing invoice data
  useEffect(() => {
    if (existingInvoice) {
      setFormData({
        clientId: existingInvoice.clientId,
        subject: existingInvoice.subject || '',
        issueDate: existingInvoice.issueDate,
        dueDate: existingInvoice.dueDate,
        items: existingInvoice.items,
        taxRate: existingInvoice.taxRate || 0,
        taxLabel: existingInvoice.taxLabel || 'Tax',
        discount: existingInvoice.discount || 0,
        discountType: existingInvoice.discountType || 'fixed',
        notes: existingInvoice.notes || '',
        terms: existingInvoice.terms || `Payment is due within ${settings?.paymentTerms || 30} days of invoice date.`
      })
    }
  }, [existingInvoice])

  // Handle default settings for new invoices when they load
  useEffect(() => {
    if (!isEditing && settings && formData.clientId === '' && formData.subject === '') {
      const days = parseInt(settings.paymentTerms) || 30
      const dueDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      const terms = days === 0
        ? 'Payment is due on receipt.'
        : `Payment is due within ${days} days of invoice date.`

      setFormData(prev => ({
        ...prev,
        dueDate,
        terms,
        taxRate: settings.taxRate || 0,
        taxLabel: settings.taxLabel || 'Tax'
      }))
    }
  }, [settings, isEditing])

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0)
  const taxAmount = subtotal * (formData.taxRate / 100)
  const discountAmount = formData.discountType === 'fixed'
    ? formData.discount
    : subtotal * (formData.discount / 100)
  const total = subtotal + taxAmount - discountAmount

  // Handlers
  const handleClientChange = (clientId) => {
    setFormData(prev => ({ ...prev, clientId }))
  }

  const handleDateChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleItemChange = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === itemId
          ? { ...item, [field]: field === 'description' ? value : Number(value) }
          : item
      )
    }))
  }

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: `item_${Date.now()}`,
          description: '',
          quantity: 1,
          rate: 0,
          tax: formData.taxRate
        }
      ]
    }))
  }

  const removeItem = (itemId) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter(item => item.id !== itemId)
      }))
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const invoiceData = {
        ...formData,
        subtotal,
        taxAmount,
        discount: discountAmount,
        total
      }

      if (isEditing) {
        // Smart Status Update
        if (existingInvoice) {
          // Determine authentic paid amount
          let previouslyPaid = existingInvoice.partialAmount || 0

          // Fallback for legacy data: if paid but no partialAmount record, assume full original total was paid
          if (existingInvoice.status === 'paid' && !existingInvoice.partialAmount) {
            previouslyPaid = existingInvoice.total
          }

          if (previouslyPaid > 0) {
            // Check if new total exceeds what was paid
            if (total > previouslyPaid) {
              invoiceData.status = 'partial'
              // Ensure partialAmount is preserved/set for consistency
              if (!existingInvoice.partialAmount) {
                invoiceData.partialAmount = previouslyPaid
              }
            } else {
              // If total is reduced to be covered by payment, mark as paid
              invoiceData.status = 'paid'
            }
          }
        }

        await updateInvoice(id, invoiceData)
        addNotification('Invoice updated successfully', 'success')
      } else {
        await addInvoice(invoiceData)
        addNotification('Invoice created successfully', 'success')
        // Don't navigate immediately, let user see the new invoice
      }

      // Navigate to invoices list after a brief delay
      setTimeout(() => {
        navigate('/invoices')
      }, 1000)
    } catch (error) {
      addNotification('Failed to save invoice', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleSend = async () => {
    if (!formData.clientId) {
      addNotification('Please select a client first', 'error')
      return
    }

    setSending(true)
    try {
      const invoiceData = {
        ...formData,
        subtotal,
        taxAmount,
        discount: discountAmount,
        total,
        status: 'sent',
        sentAt: new Date().toISOString()
      }

      if (isEditing) {
        await updateInvoice(id, invoiceData)
      } else {
        await addInvoice(invoiceData)
      }

      addNotification('Invoice sent successfully', 'success')
      setTimeout(() => {
        navigate('/invoices')
      }, 1000)
    } catch (error) {
      addNotification('Failed to send invoice', 'error')
    } finally {
      setSending(false)
    }
  }

  const handleWhatsApp = async () => {
    if (!formData.clientId) {
      addNotification('Please select a client first', 'error')
      return
    }

    setSaving(true)
    try {
      const invoiceData = {
        ...formData,
        subtotal,
        taxAmount,
        discount: discountAmount,
        total,
        status: 'sent',
        sentAt: new Date().toISOString()
      }

      let invoiceId = id
      if (isEditing) {
        await updateInvoice(id, invoiceData)
      } else {
        invoiceId = await addInvoice(invoiceData)
      }

      // Prepare WhatsApp message
      const link = `${window.location.origin}/portal/${invoiceId}`
      const formattedAmount = total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      const dueDateStr = new Date(formData.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      const subjectLine = formData.subject ? `Subject: ${formData.subject}` : ''
      const clientDisplay = selectedClient.businessName || selectedClient.name

      const message = `Hi ${clientDisplay},\n\n${settings.businessName} has sent you Invoice ${formData.invoiceNumber || ''}.\n${subjectLine}\n\nAmount Due: $${formattedAmount}\nDue Date: ${dueDateStr}\n\nYou can view and pay securely online here:\n${link}\n\nThank you!`

      const encodedMessage = encodeURIComponent(message)
      const phone = selectedClient.phone ? selectedClient.phone.replace(/\D/g, '') : ''
      const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`

      window.open(whatsappUrl, '_blank')

      addNotification('Invoice saved and WhatsApp opened', 'success')
      navigate('/invoices')
    } catch (error) {
      addNotification('Failed to save and share invoice', 'error')
    } finally {
      setSaving(false)
    }
  }

  const selectedClient = getClientById(formData.clientId)

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-24 md:pb-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/invoices"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors btn-touch"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </Link>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Invoice' : 'New Invoice'}
            </h2>
            {isEditing && (
              <p className="text-sm text-gray-500 mt-0.5">
                {existingInvoice?.invoiceNumber}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={() => setShowPreview(true)}
            className="btn btn-secondary text-sm md:text-base px-3 md:px-4"
            title="Preview"
          >
            <Eye size={18} />
            <span className="hidden sm:inline">Preview</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-secondary text-sm md:text-base px-3 md:px-4"
            title="Save Draft"
          >
            <Save size={18} />
            <span className="hidden sm:inline">Save Draft</span>
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !formData.clientId}
            className="btn btn-primary text-sm md:text-base px-3 md:px-4"
          >
            <Send size={18} />
            <span className="hidden sm:inline">Email Invoice</span>
            <span className="sm:hidden">Email</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Selection */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Business & Client Information</h3>

            {/* Business Logo & Info Preview */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100 flex items-start gap-4">
              {settings?.logo ? (
                <img src={settings.logo} alt="Business Logo" className="h-16 w-auto object-contain" />
              ) : (
                <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center text-gray-400">
                  <span className="text-xs">No Logo</span>
                </div>
              )}
              <div>
                <p className="font-bold text-gray-900">{settings?.businessName || 'Your Business Name'}</p>
                <p className="text-sm text-gray-600 whitespace-pre-line">{settings?.businessAddress}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Client *
              </label>
              <select
                value={formData.clientId}
                onChange={(e) => handleClientChange(e.target.value)}
                className="input"
                required
              >
                <option value="">Choose a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.email}
                  </option>
                ))}
              </select>

              {selectedClient && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">{selectedClient.name}</p>
                  <p className="text-sm text-gray-600">{selectedClient.email}</p>
                  <p className="text-sm text-gray-600">{selectedClient.phone}</p>
                  <p className="text-sm text-gray-500 mt-2 whitespace-pre-line">
                    {selectedClient.address}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Invoice Details */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Invoice Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Subject (Optional)
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="e.g. Website Redesign Project"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Date
                </label>
                <div className="relative">
                  <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={formData.issueDate}
                    onChange={(e) => handleDateChange('issueDate', e.target.value)}
                    className="input pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date
                </label>
                <div className="relative">
                  <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleDateChange('dueDate', e.target.value)}
                    className="input pl-10"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="font-semibold text-gray-900">Line Items</h3>
              <button
                onClick={addItem}
                className="btn btn-secondary text-sm py-1.5"
              >
                <Plus size={16} />
                Add Item
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              {/* Desktop Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 px-2 pb-2">
                <div className="col-span-6">Description</div>
                <div className="col-span-2">Quantity</div>
                <div className="col-span-2 text-right">Rate</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>

              {/* Items List */}
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div
                    key={item.id}
                    className="group relative bg-white md:bg-transparent border md:border-0 rounded-xl md:rounded-0 p-4 md:p-0 shadow-sm md:shadow-none animate-scale-in"
                  >
                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-6">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                          placeholder="Item description"
                          className="input"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                          min="0"
                          step="0.01"
                          className="input text-center"
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="relative">
                          <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                            min="0"
                            step="0.01"
                            className="input pl-8 text-right"
                          />
                        </div>
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-right font-medium text-gray-900">
                          ${(item.quantity * item.rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          disabled={formData.items.length === 1}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-30"
                          title="Remove item"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden space-y-3">
                      <div className="flex justify-between items-start gap-2">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Item {index + 1}</span>
                        <button
                          onClick={() => removeItem(item.id)}
                          disabled={formData.items.length === 1}
                          className="p-1.5 text-red-500 bg-red-50 rounded-lg disabled:opacity-30"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Description</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                          placeholder="What was this for?"
                          className="input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Qty</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                            className="input"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Rate</label>
                          <div className="relative">
                            <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                              className="input pl-8"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-baseline pt-2 border-t border-gray-100 italic">
                        <span className="text-sm text-gray-500">Line Total:</span>
                        <span className="text-base font-bold text-gray-900">
                          ${(item.quantity * item.rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Item Bottom Button (Mobile Only) */}
              <button
                onClick={addItem}
                className="md:hidden w-full btn btn-secondary border-dashed border-2 py-3 text-gray-500 flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                <span>Add Another Item</span>
              </button>
            </div>
          </div>

          {/* Notes & Terms */}
          <div className="card p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Notes & Terms</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="input"
                  placeholder="Add notes for your client..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Terms
                </label>
                <textarea
                  value={formData.terms}
                  onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                  rows={2}
                  className="input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Totals */}
        <div className="space-y-6">
          {/* Totals Card */}
          <div className="card p-6 sticky top-24">
            <h3 className="font-semibold text-gray-900 mb-4">Invoice Summary</h3>

            <div className="space-y-3 pb-4 border-b border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
              </div>

              {/* Tax */}
              <div className="flex justify-between text-sm items-center">
                <span className="text-gray-500">{formData.taxLabel} ({formData.taxRate}%)</span>
                <span className="font-medium text-gray-900">${taxAmount.toFixed(2)}</span>
              </div>

              {/* Discount */}
              <div className="flex justify-between text-sm items-center">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Discount</span>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                    className="text-xs border border-gray-200 rounded px-1 py-0.5"
                  >
                    <option value="fixed">$</option>
                    <option value="percent">%</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    -{formData.discountType === 'fixed' ? '$' : ''}{discountAmount.toFixed(2)}{formData.discountType === 'percent' ? '%' : ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="py-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-primary-600">
                  ${total.toFixed(2)}
                </span>
              </div>
              {selectedClient && (
                <p className="text-sm text-gray-500 mt-2 text-right">
                  {selectedClient.currency || 'USD'}
                </p>
              )}
            </div>

            {/* Discount Input */}
            <div className="pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apply Discount
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  {formData.discountType === 'fixed' ? (
                    <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  ) : (
                    <Percent size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  )}
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount: Number(e.target.value) }))}
                    min="0"
                    className={`input ${formData.discountType === 'fixed' ? 'pl-8' : 'pl-8'}`}
                  />
                </div>
                <button
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      discount: 0
                    }))
                  }}
                  className="btn btn-secondary text-sm px-3"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Tax Input */}
            <div className="pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tax Rate (%)
              </label>
              <div className="relative">
                <Percent size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={formData.taxRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, taxRate: Number(e.target.value) }))}
                  min="0"
                  max="100"
                  step="0.1"
                  className="input pl-8"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 space-y-3">
              <button
                onClick={handleWhatsApp}
                disabled={saving || !formData.clientId}
                className="btn btn-primary w-full"
              >
                <MessageCircle size={18} />
                {saving ? 'Saving...' : 'Save & WhatsApp'}
              </button>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-secondary text-sm"
                >
                  <Save size={16} />
                  Save Draft
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || !formData.clientId}
                  className="btn btn-secondary text-sm"
                >
                  <Send size={16} />
                  Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Mobile Footer Actions */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-40 shadow-2xl safe-bottom">
        <div className="flex gap-3 max-w-lg mx-auto">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-secondary flex-1"
          >
            <Save size={18} />
            <span>Draft</span>
          </button>
          <button
            onClick={handleWhatsApp}
            disabled={saving || !formData.clientId}
            className="btn btn-primary flex-[2] py-3 shadow-lg shadow-primary-200"
          >
            <MessageCircle size={18} />
            <span>{saving ? 'Saving...' : 'Save & WhatsApp'}</span>
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <InvoicePreview
          invoice={formData}
          client={selectedClient}
          settings={settings}
          subtotal={subtotal}
          taxAmount={taxAmount}
          discountAmount={discountAmount}
          total={total}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  )
}

// Invoice Preview Component
function InvoicePreview({ invoice, client, settings, subtotal, taxAmount, discountAmount, total, onClose }) {
  const { addNotification } = useApp()

  const handleDownload = () => {
    addNotification('PDF download initiated', 'info')
    // PDF generation would be implemented here
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Invoice Preview</h3>
          <div className="flex gap-3">
            <button onClick={handleDownload} className="btn btn-secondary text-sm">
              <Download size={16} />
              Download PDF
            </button>
            <button onClick={onClose} className="btn btn-primary text-sm">
              <Check size={16} />
              Done
            </button>
          </div>
        </div>

        {/* Invoice Preview Content */}
        <div className="overflow-auto max-h-[calc(90vh-80px)] p-8">
          <div className="bg-white max-w-3xl mx-auto shadow-lg rounded-xl p-8 lg:p-12">
            {/* Header */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">5</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">INVOICE</h1>
                <p className="text-gray-500 mt-1">#{invoice.invoiceNumber || 'DRAFT'}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-900">{settings.businessName}</p>
                <p className="text-sm text-gray-500 whitespace-pre-line">{settings.businessAddress}</p>
                <p className="text-sm text-gray-500">{settings.businessEmail}</p>
              </div>
            </div>

            {/* Client & Invoice Info */}
            <div className="flex justify-between mb-8">
              <div>
                <p className="text-sm text-gray-500 mb-1">Bill To</p>
                {client?.businessName && (
                  <p className="font-bold text-gray-900">{client.businessName}</p>
                )}
                <p className={`${client?.businessName ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                  {client?.name || 'Select a client'}
                </p>
                <p className="text-sm text-gray-600 whitespace-pre-line">{client?.address}</p>
                <p className="text-xs text-gray-400 mt-1">{client?.email}</p>
              </div>
              <div className="text-right">
                <div className="mb-2">
                  <p className="text-sm text-gray-500">Issue Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(invoice.issueDate).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <table className="w-full mb-8">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 text-sm font-semibold text-gray-600">Description</th>
                  <th className="text-center py-3 text-sm font-semibold text-gray-600">Qty</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-600">Rate</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-600">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 text-gray-900">{item.description || '—'}</td>
                    <td className="py-3 text-center text-gray-600">{item.quantity}</td>
                    <td className="py-3 text-right text-gray-600">${item.rate.toFixed(2)}</td>
                    <td className="py-3 text-right font-medium text-gray-900">
                      ${(item.quantity * item.rate).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-900">${subtotal.toFixed(2)}</span>
                </div>
                {invoice.taxRate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{invoice.taxLabel} ({invoice.taxRate}%)</span>
                    <span className="text-gray-900">${taxAmount.toFixed(2)}</span>
                  </div>
                )}
                {invoice.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">
                      Discount {invoice.discountType === 'percent' ? `(${invoice.discount}%)` : ''}
                    </span>
                    <span className="text-green-600">-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t-2 border-gray-200 pt-2">
                  <span>Total</span>
                  <span className="text-primary-600">${total.toFixed(2)}</span>
                </div>
                {settings?.enableExchange && settings?.exchangeRate > 0 && (
                  <div className="flex justify-between text-sm italic text-gray-500 pt-1">
                    <span>Equivalent in {settings.exchangeCurrency || 'GYD'}</span>
                    <span>
                      {(total * settings.exchangeRate).toLocaleString('en-US', {
                        style: 'currency',
                        currency: settings.exchangeCurrency || 'GYD'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {(invoice.notes || invoice.terms) && (
              <div className="border-t border-gray-200 pt-6">
                {invoice.notes && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                    <p className="text-sm text-gray-600">{invoice.notes}</p>
                  </div>
                )}
                {invoice.terms && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Terms</p>
                    <p className="text-sm text-gray-600">{invoice.terms}</p>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
              <p>Thank you for your business!</p>
              <p className="mt-1">{settings.businessEmail}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
